import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import {
  getPolicy,
  payRenewal,
  PayRenewalResult,
  PolicySummary,
} from "../api/services";
import { PaymentMethodPicker } from "../components/payment";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import {
  chargeAmountForRenewalTerm,
  payableRenewalLateFee,
  allowedPaymentMethodType,
} from "@libs/quote";
import { formatDate as formatDateOnly, parseLocalDate } from "@ui/shared";
import { formatCurrency } from "../utils/formatCurrency";

const usd = formatCurrency;

// Renders DATE-only fields (nextTerm.startsOn / endsOn). parseLocalDate
// keeps the calendar day stable across US timezones.
const formatDate = (s: string | null | undefined): string =>
  s
    ? formatDateOnly(s, { year: "numeric", month: "numeric", day: "numeric" })
    : "—";

const PolicyRenewal: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [policy, setPolicy] = useState<PolicySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { methods, loading: methodsLoading } = usePaymentMethods();
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState<PayRenewalResult | null>(null);
  // ACH authorization acceptance. Required server-side for any bank-
  // account PM — legacy accounts attached without the Plaid+Stripe link
  // flow have no Nacha mandate on file and Stripe rejects the charge
  // otherwise. Reset when the selected PM changes.
  const [mandateAccepted, setMandateAccepted] = useState(false);
  useEffect(() => {
    setMandateAccepted(false);
  }, [selectedMethodId]);

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);

    getPolicy(uuid)
      .then((res) => {
        if (!cancelled) setPolicy(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else {
          console.error("Failed to load policy:", err);
          setError(t("policyRenewal.errorLoading"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uuid, t]);

  // Card-only when the renewal term starts within 5 days — ACH wouldn't
  // clear in time. Computed here (not after the loading guards) because the
  // default-selection effect below has to respect it.
  const allowedType = allowedPaymentMethodType(policy?.nextTerm?.startsOn);
  const cardRequired = allowedType === "card";
  const isBankMethod = (m: { type: string }) => m.type !== "card";
  const allowedMethods = methods.filter((m) =>
    allowedType === null
      ? true
      : allowedType === "bank"
        ? isBankMethod(m)
        : !isBankMethod(m),
  );

  useEffect(() => {
    if (methods.length === 0) return;
    // Ignore a default of the disallowed type, and drop an already-selected
    // method that no longer qualifies — otherwise the tenant submits
    // something the server will reject.
    const pool = allowedMethods;
    const current = methods.find((m) => m.id === selectedMethodId) ?? null;
    if (current && !pool.some((m) => m.id === current.id)) {
      const replacement = pool.find((m) => m.isDefault) ?? pool[0] ?? null;
      setSelectedMethodId(replacement ? replacement.id : null);
      return;
    }
    if (selectedMethodId !== null) return;
    const def = pool.find((m) => m.isDefault) ?? pool[0];
    if (def) setSelectedMethodId(def.id);
  }, [methods, selectedMethodId, allowedMethods]);

  if (loading) {
    return (
      <div>
        <GoBack to="/dashboard" />
        <p className="text-sm text-muted">{t("policyRenewal.loading")}</p>
      </div>
    );
  }

  if (notFound || !policy) {
    return (
      <div>
        <GoBack to="/dashboard" />
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error || t("policyRenewal.notFound")}
        </div>
      </div>
    );
  }

  const nextTerm = policy.nextTerm;
  // Quote total is the 12-month figure for month-to-month terms; what
  // the tenant actually owes for a single 3-month renewal is total / 4.
  // `chargeAmountForRenewalTerm` handles both cases (fixed = identity).
  const baseAmount =
    chargeAmountForRenewalTerm(nextTerm?.total, nextTerm?.renewalTerm) ?? 0;
  // A renewal paid after its term has started carries a flat late fee. Same
  // helper the server uses in payRenewal, so the figure shown here is exactly
  // what gets charged.
  const lateFee = payableRenewalLateFee(nextTerm);
  const renewalAmount = baseAmount + lateFee;

  // Renewal is only payable within ~100 days of `startsOn` — keep the
  // window in sync with the Dashboard's renewal-CTA visibility check.
  // Prevents tenants from paying months early via a direct URL.
  const RENEWAL_VISIBLE_WINDOW_DAYS = 100;
  const renewalWithinWindow = (() => {
    if (!nextTerm?.startsOn) return false;
    // nextTerm.startsOn is YYYY-MM-DD — parse as local midnight so the
    // day delta vs Date.now() is on the correct calendar day.
    const start = parseLocalDate(nextTerm.startsOn);
    if (isNaN(start.getTime())) return false;
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysUntilStart = Math.floor(
      (start.getTime() - Date.now()) / msPerDay,
    );
    return daysUntilStart <= RENEWAL_VISIBLE_WINDOW_DAYS;
  })();

  // Confirmed terms can be paid regardless of the 100-day window — the
  // landlord explicitly opted them in, so timing isn't a gate.
  const renewalAvailableNow =
    renewalWithinWindow || nextTerm?.status === "confirmed";

  const renewalNotPayable =
    !nextTerm ||
    (nextTerm.status !== "scheduled" && nextTerm.status !== "confirmed") ||
    renewalAmount <= 0 ||
    !renewalAvailableNow;

  const hasAllowedMethod = allowedMethods.length > 0;

  const selectedMethod = methods.find((m) => m.id === selectedMethodId) ?? null;
  const isBankSelected =
    selectedMethod !== null && selectedMethod.type !== "card";

  const handlePay = async () => {
    if (!selectedMethodId || renewalNotPayable) return;
    if (isBankSelected && !mandateAccepted) return;
    setPaying(true);
    setError(null);
    try {
      const res = await payRenewal(
        policy.uuid,
        selectedMethodId,
        isBankSelected ? true : undefined,
      );
      setResult(res.data);
      if (res.data.status === "succeeded") {
        setTimeout(() => navigate("/dashboard"), 1500);
      }
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("policyRenewal.errorDefault"),
      );
    } finally {
      setPaying(false);
    }
  };

  if (result?.status === "succeeded") {
    return (
      <div>
        <GoBack to="/dashboard" />
        <div className="rounded-xl bg-white shadow-sm">
          <div className="p-10 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
              <svg
                className="h-7 w-7 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-primary">
              {t("policyRenewal.success.title")}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {t("policyRenewal.success.body", {
                amount: usd(result.amount / 100),
              })}
            </p>
            <p className="mt-1 text-xs text-muted">
              {t("policyRenewal.success.redirecting")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (result?.status === "processing") {
    return (
      <div>
        <GoBack to="/dashboard" />
        <div className="rounded-xl bg-white shadow-sm">
          <div className="p-10 text-center">
            <h3 className="text-lg font-semibold text-primary">
              {t("policyRenewal.processingState.title")}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {t("policyRenewal.processingState.body")}
            </p>
            <button
              type="button"
              className="mt-6 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
              onClick={() => navigate("/dashboard")}
            >
              {t("policyRenewal.processingState.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canPay =
    !!selectedMethodId &&
    !renewalNotPayable &&
    !paying &&
    methods.length > 0 &&
    !methodsLoading &&
    // Bank PMs require the ACH authorization box to be checked.
    (!isBankSelected || mandateAccepted);

  return (
    <div>
      <GoBack to="/dashboard" />

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {result && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {result.error ||
            t("policyRenewal.errorWithStatus", { status: result.status })}
        </div>
      )}

      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="font-semibold text-primary">
            {t("policyRenewal.headerTitle")}
          </h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            <div className="md:col-span-8">
              <h5 className="mb-2 text-primary">
                {policy.unit?.property?.name ?? ""} – {policy.unit?.name ?? ""}
              </h5>
              {policy.unit?.property?.address && (
                <p className="mb-2 text-muted">
                  {policy.unit.property.address}
                </p>
              )}
              <p>
                <strong>{t("policyRenewal.policyLabel")}:</strong> {policy.name}
              </p>
              {nextTerm && (
                <p className="mt-1 text-sm text-muted">
                  {t("policyRenewal.coverageRange", {
                    start: formatDate(nextTerm.startsOn),
                    end: formatDate(nextTerm.endsOn),
                  })}
                </p>
              )}
            </div>
            <div className="md:col-span-4 md:text-right">
              <h4 className="mb-1 text-primary">
                {t("policyRenewal.amountDue", {
                  amount: usd(renewalAmount),
                })}
              </h4>
              <small className="text-muted">
                {t("policyRenewal.amountSubtitle")}
              </small>
              {lateFee > 0 && (
                <div className="mt-1 text-sm text-danger">
                  Includes a {usd(lateFee)} late fee — this renewal was due{" "}
                  {nextTerm?.startsOn
                    ? formatDate(nextTerm.startsOn)
                    : "earlier"}
                  .
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h5 className="font-semibold text-primary">
            {t("policyRenewal.chooseMethod")}
          </h5>
          <button
            type="button"
            className="cursor-pointer text-sm font-medium text-info underline hover:text-sky-600"
            onClick={() => navigate("/payment-methods")}
          >
            {t("policyRenewal.manageMethods")}
          </button>
        </div>
        <div className="p-5">
          {allowedType !== null && (
            <div className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-warning">
              {cardRequired
                ? t("policyRenewal.cardOnlyBanner")
                : t(
                    "policyRenewal.bankOnlyBanner",
                    "Because your renewal starts more than 5 days from now, this payment must be made from a bank account.",
                  )}
            </div>
          )}

          {methodsLoading ? (
            <p className="text-sm text-muted">
              {t("policyRenewal.loadingMethods")}
            </p>
          ) : allowedType !== null && !hasAllowedMethod ? (
            // No method of the required type — the picker would render only
            // disabled rows, so point the tenant at Payment Methods instead.
            <div className="rounded-lg border-2 border-dashed border-amber-200 bg-amber-50/50 p-5 text-center">
              <p className="mb-3 text-sm text-gray-700">
                {cardRequired
                  ? t("policyRenewal.cardOnlyAddPrompt")
                  : t(
                      "policyRenewal.bankOnlyAddPrompt",
                      "You'll need a bank account to pay for this renewal. Add one to continue.",
                    )}
              </p>
              <button
                type="button"
                className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
                onClick={() => navigate("/payment-methods")}
              >
                {cardRequired
                  ? t("policyRenewal.addCard")
                  : t("policyRenewal.addBank", "Add bank account")}
              </button>
            </div>
          ) : (
            <PaymentMethodPicker
              methods={methods}
              selectedId={selectedMethodId}
              onSelect={setSelectedMethodId}
              emptyMessage={t("policyRenewal.emptyMethods")}
              allowedType={allowedType}
              disabledHint={
                cardRequired
                  ? t("policyRenewal.cardOnlyHint")
                  : allowedType === "bank"
                    ? t(
                        "policyRenewal.bankOnlyHint",
                        "Your renewal starts more than 5 days from now — pay from a bank account.",
                      )
                    : undefined
              }
            />
          )}
        </div>
      </div>

      {isBankSelected && (
        <label className="mb-6 flex items-start gap-3 rounded-xl bg-white px-5 py-4 text-sm text-gray-700 shadow-sm cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 cursor-pointer"
            checked={mandateAccepted}
            onChange={(e) => setMandateAccepted(e.target.checked)}
            disabled={paying}
          />
          <span>
            {t("policyRenewal.achAuthorization", {
              amount: usd(renewalAmount),
            })}
          </span>
        </label>
      )}

      <div className="mb-6 flex flex-col items-end gap-2">
        <button
          className="cursor-pointer rounded-lg bg-primary px-8 py-3 text-base font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canPay}
          onClick={handlePay}
        >
          {paying
            ? t("policyRenewal.processing")
            : t("policyRenewal.payButton", { amount: usd(renewalAmount) })}
        </button>
        <small className="text-muted">
          {t("policyRenewal.payDisclaimer", { amount: usd(renewalAmount) })}
        </small>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <h6 className="text-sm font-semibold">
            {t("policyRenewal.securityTitle")}
          </h6>
          <p className="text-sm text-muted">
            {t("policyRenewal.securityBody")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PolicyRenewal;
